// src/pages/Marketplace/markets/BusinessesInMarket.tsx
import React, { useState } from 'react';
import { ErrorBanner, Spinner } from '@/pages/Admin/shared';
import { useMarketBusinesses, useMarkets } from '@/pages/Marketplace/shared';
import type { Market, MarketBusiness } from '@/pages/Marketplace/shared';
import OverrideAssignmentModal from './OverrideAssignmentModal';

interface Props { market: Market; apiUrl: string; accessToken: string | null; }

export default function BusinessesInMarket({ market, apiUrl, accessToken }: Props): React.ReactElement {
  const { items, cursor, loading, error, loadMore, refetch } =
    useMarketBusinesses(apiUrl, accessToken, market.id);
  const { data: markets } = useMarkets(apiUrl, accessToken);
  const [overriding, setOverriding] = useState<MarketBusiness | null>(null);

  return (
    <section className="mt-6">
      <h2 className="text-sm font-semibold text-white mb-2">Businesses in this market</h2>
      {error && <ErrorBanner message={error} />}
      <div className="border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-gray-400 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">City</th>
              <th className="text-left px-3 py-2">Assigned via</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-gray-800">
                <td className="px-3 py-2">{b.name}</td>
                <td className="px-3 py-2 text-gray-400">{b.city ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                    b.assigned_via === 'overridden'
                      ? 'border-amber-700/50 text-amber-300 bg-amber-950/40'
                      : 'border-gray-700 text-gray-400 bg-gray-900'
                  }`}>{b.assigned_via}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setOverriding(b)} className="text-xs text-violet-300 hover:text-violet-200">
                    Override
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="p-3"><Spinner size="sm" /></div>}
        {cursor && !loading && (
          <button onClick={loadMore} className="w-full text-center text-xs text-gray-400 hover:text-white py-2 border-t border-gray-800">
            Load more
          </button>
        )}
      </div>

      {overriding && markets && (
        <OverrideAssignmentModal
          business={overriding}
          markets={markets}
          apiUrl={apiUrl}
          accessToken={accessToken}
          onClose={() => setOverriding(null)}
          onSaved={refetch}
        />
      )}
    </section>
  );
}
